// Payments/PaymentLinksPage.js

import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Alert, Container, useTheme, alpha, Divider, Skeleton
} from '@mui/material';
import CreatePaymentLinkForm from './CreatePaymentLinkForm';
import PaymentLinksTable from './PaymentLinksTable';
import { hasPermission } from '../../../utils/permissionUtils';
import LinkIcon from '@mui/icons-material/Link';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';

const FONT = "'Plus Jakarta Sans', sans-serif";

const PaymentLinksPage = ({ refreshKey, onRefresh, cryptoConfig }) => {
  const theme = useTheme();

  const handleRefresh = () => onRefresh(prev => prev + 1);

  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  const [checkingPermissions, setCheckingPermissions] = useState(true);

  useEffect(() => {
    const checkPermissions = async () => {
      setCheckingPermissions(true);
      try {
        const viewPerm = await hasPermission('payments', 'view');
        const addPerm = await hasPermission('payments', 'add');
        const editPerm = await hasPermission('payments', 'edit');
        const deletePerm = await hasPermission('payments', 'delete');

        setCanView(viewPerm);
        setCanAdd(addPerm);
        setCanEdit(editPerm);
        setCanDelete(deletePerm);
      } catch (error) {
        console.error('Error checking permissions:', error);
        setCanView(false);
        setCanAdd(false);
        setCanEdit(false);
        setCanDelete(false);
      } finally {
        setCheckingPermissions(false);
      }
    };

    checkPermissions();
  }, []);

  if (checkingPermissions) {
    return (
      <Box sx={{ py: 2 }}>
        <Skeleton variant="text" width={260} height={36} sx={{ mb: 1 }} />
        <Skeleton variant="text" width={180} height={20} sx={{ mb: 3 }} />
        <Skeleton variant="rounded" height={200} sx={{ mb: 3, borderRadius: 3 }} />
        <Skeleton variant="rounded" height={320} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  if (!canView) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert
          severity="error"
          sx={{
            borderRadius: 3,
            fontFamily: FONT,
            border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
            '& .MuiAlert-message': { width: '100%' },
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: FONT }}>
            Access Denied
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5, fontFamily: FONT, color: 'text.secondary' }}>
            You don't have permission to view Payment Link settings. Please contact your administrator.
          </Typography>
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h5"
          component="h1"
          sx={{ fontWeight: 800, fontFamily: FONT, letterSpacing: '-0.02em' }}
          color="text.primary"
        >
          Payment Links
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, fontFamily: FONT, color: 'text.secondary' }}
        >
          Create and manage payment links for your customers
        </Typography>
      </Box>

      {/* Create New Link Section */}
      {canAdd && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.background.paper,
            mb: 3,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: 3,
              py: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
              bgcolor: alpha(theme.palette.text.primary, 0.015),
            }}
          >
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                color: theme.palette.primary.main,
              }}
            >
              <LinkIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 700, fontFamily: FONT, fontSize: '0.95rem' }}
              color="text.primary"
            >
              Create New Link
            </Typography>
          </Box>
          <Box sx={{ p: 3 }}>
            <CreatePaymentLinkForm
              onSuccess={handleRefresh}
              permission={{ canAdd, canEdit, canDelete }}
              cryptoConfig={cryptoConfig}
            />
          </Box>
        </Paper>
      )}

      {/* Manage Links Section */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background.paper,
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            borderBottom: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.text.primary, 0.015),
          }}
        >
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
            }}
          >
            <TableChartOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 700, fontFamily: FONT, fontSize: '0.95rem' }}
            color="text.primary"
          >
            Manage Links
          </Typography>
        </Box>
        <Box sx={{ p: 0 }}>
          <PaymentLinksTable
            refreshKey={refreshKey}
            onRefresh={handleRefresh}
            permission={{ canAdd, canEdit, canDelete }}
          />
        </Box>
      </Paper>
    </Box>
  );
};

export default PaymentLinksPage;
